import React from "react";
import { Link } from "react-router-dom";
import { useLocale } from "@/contexts/LocaleContext";
import { ArrowRight, Building2, Clock3, ShieldCheck, Users2, CheckCircle2, Sparkles, GaugeCircle } from "lucide-react";

const FEATURES = [
  {
    icon: ShieldCheck,
    title: "GDPR-ready by design",
    body: "Retention policies, full audit trail, evacuation-mode exports. Built for UK/IE workplaces.",
  },
  {
    icon: Users2,
    title: "Contractors, visitors, staff",
    body: "Pre-registrations, induction flows, RAMS + insurance tracking, permits-to-work — one place.",
  },
  {
    icon: Clock3,
    title: "Sign in under 10 seconds",
    body: "Tablet kiosk mode for reception desks. Works with iPad, Surface, any 1024×768 tablet.",
  },
];

const TIERS = [
  // Phase B2 — pricing values are MINOR units (pence). The displayed string
  // is locale-formatted by the page below; the product itself is GBP.
  { name: "Solo", price_minor: 1200, desc: "1 site, 100 visits / month. Perfect for small offices." },
  { name: "Standard", price_minor: 2900, desc: "1 site, unlimited visits. Full feature set." },
  { name: "Pro", price_minor: 6900, desc: "5 sites, unlimited visits. Multi-location HQs." },
];

export default function Landing() {
  // Phase B2 — viewer-locale-aware price rendering. The brief calls this out
  // explicitly: products stay GBP, only the way GBP is displayed varies
  // (en-US viewers see "£12.00", not "$12.00").
  const { formatCurrency } = useLocale();
  return (
    <div className="min-h-screen bg-white text-navy-900">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-navy-100" data-testid="landing-header">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-lg bg-navy-900 flex items-center justify-center">
              <GaugeCircle className="w-5 h-5 text-orange-500" />
            </div>
            <span className="font-bold tracking-tight">EntryBook</span>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/login" className="btn-ghost" data-testid="landing-login-link">Log in</Link>
            <Link to="/signup" className="btn-accent" data-testid="landing-signup-cta">
              Start 14-day trial <ArrowRight className="w-4 h-4" />
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section
        className="relative overflow-hidden bg-navy-900 text-white px-6 py-20 md:py-32 grain"
        data-testid="landing-hero"
      >
        <div className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, rgba(255,107,53,0.35), transparent 40%), radial-gradient(circle at 80% 80%, rgba(255,107,53,0.18), transparent 45%)",
          }}
        />
        <div className="relative max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-in-up">
            <div className="eb-overline mb-5 text-orange-400">Visitor + Contractor sign-in</div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Reception, <span className="text-orange-500">without</span> the clipboard.
            </h1>
            <p className="mt-6 text-navy-100/90 text-lg max-w-lg">
              EntryBook replaces paper sign-in books with a tablet kiosk your visitors will actually want to use —
              plus contractor inductions, RAMS, permits & evacuation mode built in.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className="btn-accent text-base px-6 py-3" data-testid="hero-signup">
                Start free 14-day trial <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/login" className="btn-outline text-base px-6 py-3 bg-white/5 text-white border-white/20 hover:bg-white/10" data-testid="hero-login">
                Log in
              </Link>
            </div>
            <p className="mt-6 text-sm text-navy-200 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-orange-400" /> No credit card · Cancel anytime · UK/IE data residency
            </p>
          </div>
          <div className="relative">
            <div className="aspect-[4/3] rounded-2xl overflow-hidden ring-1 ring-white/10 shadow-lift">
              <img
                src="https://images.unsplash.com/photo-1759143545924-beb85b33c0f1?crop=entropy&cs=srgb&fm=jpg&q=85"
                alt="Reception tablet kiosk"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 -left-5 bg-white text-navy-900 px-4 py-3 rounded-xl shadow-card flex items-center gap-3" data-testid="hero-floating-stat">
              <Sparkles className="w-5 h-5 text-orange-500" />
              <div>
                <div className="text-xs text-navy-500">Average sign-in</div>
                <div className="font-bold text-lg leading-none">8 seconds</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-28" data-testid="landing-features">
        <div className="max-w-2xl mb-14">
          <div className="eb-overline mb-3">What's inside</div>
          <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
            Designed for the way <span className="text-orange-600">UK workplaces</span> actually run.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="eb-card p-7 animate-fade-in-up" data-testid={`feature-${f.title.split(' ')[0].toLowerCase()}`}>
                <div className="w-11 h-11 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-navy-600 leading-relaxed">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Pricing teaser */}
      <section className="bg-surface px-6 py-20 md:py-28" data-testid="landing-pricing">
        <div className="max-w-6xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="eb-overline mb-3">Pricing</div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight">
              Simple plans. One month free if you prepay annually.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TIERS.map((t, idx) => (
              <div
                key={t.name}
                className={`eb-card p-8 relative ${idx === 1 ? "border-orange-500 ring-2 ring-orange-200" : ""}`}
                data-testid={`landing-plan-${t.name.toLowerCase()}`}
              >
                {idx === 1 && (
                  <span className="absolute -top-3 left-8 bg-orange-500 text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
                    Most popular
                  </span>
                )}
                <h3 className="text-xl font-semibold">{t.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-bold">{formatCurrency(t.price_minor, "GBP")}</span>
                  <span className="text-sm text-navy-500">/ month</span>
                </div>
                <p className="mt-4 text-sm text-navy-600">{t.desc}</p>
                <Link
                  to="/signup"
                  className={`mt-6 inline-flex w-full justify-center ${idx === 1 ? "btn-accent" : "btn-outline"}`}
                  data-testid={`landing-plan-cta-${t.name.toLowerCase()}`}
                >
                  Start free trial
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-navy-100 px-6 py-10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-navy-500">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4" /> EntryBook · Built for UK & Ireland
          </div>
          <div>© {new Date().getFullYear()} EntryBook. All rights reserved.</div>
        </div>
      </footer>
    </div>
  );
}
